import { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { UserAuth } from "../context/AuthContext";
import SocialAuthButton from "./SocialAuthButton";
import { Eye, EyeOff } from "lucide-react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Link,
  Spinner,
  Heading,
  Image,
} from "@chakra-ui/react";
import bgleft from "../../assets/supbase/bgleft.svg";
import waveflowStudioLogo from "../../assets/supbase/waveflowStudioLogo.svg";
import { setAgentdataclearData, waveflowclearefetchagentsData } from "../../redux/reducer/waveFlow";
import { useDispatch } from "react-redux";

const Signin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const dispatch = useDispatch();
  const { session } = UserAuth();
  
  const token = session?.access_token;
  const user = session?.user;
  const user_name = session?.user?.user_metadata?.display_name;

  const { signInUser, signInWithGoogle, signInWithGithub } = UserAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (session?.access_token) {
      localStorage.setItem("token", session.access_token);
      if (session.user?.email) localStorage.setItem("user_mailId", session.user.email);
      if (session.user?.user_metadata?.display_name) {
        localStorage.setItem("user_name", session.user.user_metadata.display_name);
      }
      navigate("/");
    }
  }, [session, navigate]);

  // DISABLED: OAuth temporarily removed during Cognito migration.
  // Email/password authentication only.
  /*
  const handleSocialSignIn = async (provider) => {
    setSocialLoading(provider);
    const result =
      provider === "google"
        ? await signInWithGoogle()
        : await signInWithGithub();
    setSocialLoading(null);
    if (result.error) setError(result.error.message);
  };
  */

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await signInUser(email, password);

    if (!result.success) {
      setError(result.error || "Failed to sign in.");
      if (result.needsConfirmation) {
        setTimeout(() => {
          navigate(`/auth/verify-email?email=${encodeURIComponent(email)}`);
        }, 2000);
      }
      setTimeout(() => setError(null), 3000);
    } else {
      const data = result.data;
      setError(null);
      // Use the data from the response instead of current session state
      if (data?.session?.access_token) {
        localStorage.setItem("token", data.session.access_token);
        localStorage.setItem("user_mailId", data.session.user?.email);
        localStorage.setItem("user_name", data.session.user?.user_metadata?.display_name);
        dispatch(waveflowclearefetchagentsData())
        dispatch(setAgentdataclearData())
        navigate("/");
      } else {
        // Fallback: wait for session update
        setTimeout(() => {
          if (session?.access_token) {
            localStorage.setItem("token", session.access_token);
            localStorage.setItem("user_mailId", session.user?.email);
            localStorage.setItem("user_name", session.user?.user_metadata?.display_name);
            navigate("/");
          }
        }, 1000);
      }
    }
    setLoading(false);
  };

  return (
    <Flex minHeight="100vh" bg="white">
      {/* Left side */}
      <Box
        flex="3"
        bgImage={`url(${bgleft})`}
        bgSize="cover"
        bgPosition="center"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          position="absolute"
          top="1rem"
          left="1rem"
          fontSize="20px"
          fontWeight={500}
          color="white"
          fontFamily="ave"
        >
          <Flex align="center">
            <Image src={waveflowStudioLogo} height="36px" width="40px" mr="1" />
            <Text as="span" fontFamily="W">
              W
            </Text>
            aveflow Studio
          </Flex>
        </Box>

        <Box textAlign="center" color="white" px={6}>
          <Heading
            fontSize="3xl"
            fontWeight={700}
            mb={6}
            lineHeight="short"
            color="#DDDFFC"
          >
            Business Workflow Automation with
            <br />
            <Box as="span" fontSize="3xl" fontWeight={700} color="#FFF">
              #Waveflow–Studio
            </Box>
          </Heading>
          <Text
            fontSize="xl"
            bgGradient="linear(to-r, blue.600, purple.600)"
            bgClip="text"
            fontWeight={500}
            color={"#F5F5F5"}
          >
            Powerful Visual Coding using Natural Language
          </Text>
        </Box>
      </Box>

      {/* Right side - Form */}
      <Flex
        flex="2"
        align="center"
        justify="center"
        p={8}
        borderLeft="1px solid"
        borderColor="gray.100"
      >
        <Box
          w="full"
          maxW="md"
          bg="white"
          p={8}
          rounded="2xl"
          shadow="lg"
          border="1px solid"
          borderColor="gray.100"
        >
          <Flex justify="space-between" mb={8} align="center">
            <Text fontSize="2xl" fontWeight="semibold" color="gray.900">
              Welcome back to the Waveflow Studio
            </Text>
          </Flex>

          {error && (
            <Box
              mb={4}
              p={3}
              bg="red.50"
              color="red.600"
              rounded="md"
              fontSize="sm"
            >
              {error}
            </Box>
          )}

          <form onSubmit={handleSignIn}>
            {/* Social Login Buttons */}
            {/* <Flex mb={4} gap={2}>
              <SocialAuthButton
                provider="google"
                onClick={() => handleSocialSignIn("google")}
                loading={socialLoading === "google"}
              />
              <SocialAuthButton
                provider="github"
                onClick={() => handleSocialSignIn("github")}
                loading={socialLoading === "github"}
              />
            </Flex> */}

            {/* Divider */}
            {/* <Box position="relative" textAlign="center" my={6}>
              <Box borderTop="1px solid" borderColor="gray.300" />
              <Text
                position="absolute"
                top="-10px"
                left="50%"
                transform="translateX(-50%)"
                bg="white"
                px={2}
                fontSize="sm"
                color="gray.500"
              >
                OR
              </Text>
            </Box> */}

            {/* Email Input */}
            <FormControl mb={4} id="email" isRequired>
              <FormLabel fontSize="sm" color="gray.700">
                EMAIL
              </FormLabel>
              <Input
                type="email"
                placeholder="yourname@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="flushed"
                focusBorderColor="blue.500"
              />
            </FormControl>

            {/* Password Input */}
            <FormControl mb={2} id="password" isRequired>
              <FormLabel fontSize="sm" color="gray.700">
                PASSWORD
              </FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  variant="flushed"
                  focusBorderColor="blue.500"
                />
                <InputRightElement>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    color="gray.400"
                    _hover={{ color: "gray.600" }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <Box textAlign="right" mb={2}>
              <Link
                as={RouterLink}
                to="/forgot-password"
                color="blue.600"
                fontSize="sm"
                _hover={{ textDecoration: "underline" }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              isLoading={loading}
              loadingText="Logging In..."
              colorScheme="blue"
              size="lg"
              w="full"
              fontWeight="medium"
              rightIcon={
                !loading && (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                )
              }
            >
              Log in
            </Button>
          </form>

          <Box mt={1} textAlign="center">
            <Text fontSize="sm" color="gray.500">
              No Account yet?{" "}
              <Link
                as={RouterLink}
                to="/signup"
                color="blue.600"
                fontWeight="medium"
                _hover={{ textDecoration: "underline", color: "blue.700" }}
              >
                Sign Up
              </Link>
            </Text>
          </Box>
        </Box>
      </Flex>
    </Flex>
  );
};

export default Signin;
